// ./src/hooks/useTopUp.ts
//
// Buying more sending allowance WITHOUT leaving the page.
//
// ⚠ THE FIRST VERSION OF THIS REDIRECTED TO STRIPE'S HOSTED CHECKOUT AND HAD TO BE REBUILT.
// Top-ups are bought from inside the composer, mid-send — recipients chosen, message written,
// preview generated. Navigating away threw all of that on the floor, so "buy more and carry
// on" became "lose your work, then decide whether to bother". A purchase flow that costs you
// the task you were doing is worse than no purchase flow at all.
//
// Now the payment happens in a drawer with Stripe's PaymentElement — the same thing guests use
// to buy tickets (`StripePayStep` in events-web). The page never navigates and the composer
// keeps every bit of its state.
//
// ⚠ THE CLIENT STILL CANNOT GRANT CREDITS. `stripe.confirmPayment` succeeding means Stripe took
// the money, NOT that the credits exist — ms_auth's webhook grants them, a moment later. So
// after a successful payment we POLL until the balance MOVES. Not "poll until non-zero": a
// host who already had credits would be told their purchase landed the instant they paid.
//
// ⚠ And a timeout is NOT a failure. The payment succeeded; only the grant is late. Saying
// "payment failed" to someone who has been charged is the worst thing this file could do.
import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";

import { AppAddonProduct, AppAddonPaymentIntent, AppAddonService } from "@/utils/api/AppAddonService";
import { AppFeatureCreditRow, AppFeatureCreditService } from "@/utils/api/AppFeatureCreditService";
import { EventGuestService } from "@/utils/api/EventGuestService";
import { creditFeatureForKind, sortPurchasables, TopUpKind } from "@/utils/topUp";
import { PURCHASE_KIND, trackPurchase } from "@/utils/analytics";

const POLL_MS = 1500;
const POLL_ATTEMPTS = 20;

export type TopUpPhase = "idle" | "creating" | "paying" | "confirming" | "arrived" | "slow";

/** What a validated code did to the price — server-computed, never worked out here. */
export interface AppliedDiscount {
  code: string;
  /** Decimals, matching the rest of this rail. */
  amountBeforeDiscount: number;
  discountAmount: number;
  amountDue: number;
  /** ⚠ Carried so the drawer can format money without guessing. A bare "$" means eight things. */
  currency: string;
}

/** One channel's spending power, as the server reports it. */
export interface MessagingBalance {
  kind?: string | null;
  /** ⚠ -1 = unlimited. null = NOT KNOWN — never render either as zero. */
  allowanceLimit?: number | null;
  allowanceUsed?: number | null;
  allowanceLeft?: number | null;
  credits?: number | null;
  totalAvailable?: number | null;
  unlimited?: boolean | null;
}

export interface UseTopUpReturn {
  /**
   * The event-relative sending balance for a kind. ⚠ `undefined` for `ai` and whenever there is
   * no event — `eventMessagingBalance` is event-scoped and reports the two SENDING counters
   * only. Callers must treat undefined as "not known", never as zero.
   */
  balance: (kind: TopUpKind) => MessagingBalance | undefined;
  /**
   * PURCHASED credits held for a kind, from the ACCOUNT wallet. `undefined` = not known yet.
   *
   * ⚠ THE WALLET, NOT THE EVENT BALANCE, AND IT IS THE SAME NUMBER. Verified against live data:
   * `eventMessagingBalance.credits` is "both scopes summed", and since A2.9.2 nothing writes the
   * event scope, so it equals `accountCredits` exactly (email 29,899 = 29,899; message 895 =
   * 895). Reading the wallet therefore changes no figure — it just works for kinds the
   * event-scoped op does not report, which is every kind that is not a SENDING counter.
   *
   * ⚠ `ai` was invisible for exactly that reason: the drawer read `balance.credits`, and
   * `eventMessagingBalance` reports email and message only. The AI credits were in the wallet
   * the whole time.
   */
  purchasedCredits: (kind: TopUpKind) => number | undefined;
  balanceLoaded: boolean;
  reloadBalance: () => Promise<void>;
  bundles: AppAddonProduct[];
  loadingBundles: boolean;
  /**
   * Load what is for sale. Either ONE credit feature's bundles, or every SERVICE.
   *
   * ⚠ The two are different questions, not one with a filter. A credit top-up asks for exactly
   * one counter's bundles; a services list asks for everything a human delivers, across
   * features. Passing a feature code for services would return one service at a time.
   */
  loadBundles: (params: { featureCode?: string; addonType?: string }) => void;
  /** The intent to pay, once a bundle is chosen. */
  intent: AppAddonPaymentIntent | null;
  phase: TopUpPhase;
  arrivedCredits: number;
  lastError: OpResult | null;
  errorText: string | null;
  /**
   * Creates the intent for a bundle; does NOT pay.
   *
   * ⚠ EXCEPT when a discount code covers the whole bundle — then there is nothing to pay and
   * the server has already granted. The phase goes straight to "arrived".
   */
  startPurchase: (sku: string, discountCode?: string | null) => Promise<void>;
  /** The discount the server accepted for the current intent, for the drawer to show. */
  discount: AppliedDiscount | null;
  /** Called by the PaymentElement step once Stripe reports the payment succeeded. */
  /** Poll until the purchased FEATURE's balance moves. Takes a feature code, not a kind. */
  onPaid: (featureCode: string) => Promise<void>;
  setPaymentError: (message: string | null) => void;
  reset: () => void;
}

/**
 * @param eventId the event whose messaging balance to read (the balance is always
 *                event-relative — it sums this event's scoped credits, the account's, and the
 *                monthly allowance, in the order a send spends them)
 *
 * ⚠ READING IS EVENT-RELATIVE; BUYING IS NOT, AND NO LONGER TAKES A SCOPE (ROADMAP A2.9.2).
 * Every purchase lands on the ACCOUNT and is spendable on any event. This hook used to take a
 * second `scopeRef` argument so the event dashboard could buy credits locked to one event; the
 * owner reversed that ruling, so there is one kind of purchase again and nothing to choose.
 *
 * ⚠ The SERVER still supports the event scope and must keep doing so — `reserveSendAllowance`
 * spends event credits → account credits → monthly allowance, and A9.4's per-event upgrades are
 * its obvious future writer. Nothing writes an event-scoped row today, so that branch simply
 * reads 0. Do not "tidy" the spend rail away because this client stopped using it.
 */
/**
 * `readOnMount` (default true): read the wallet and the balance as soon as the caller mounts — the composer shows them.
 * ⚠ #186: false for a caller whose drawer is the only thing that shows them (the event dashboard's ⋯ menu); it reads
 * when the drawer opens (`reloadBalance`), not on every visit.
 */
export const useTopUp = (eventId?: string | null, options?: { readOnMount?: boolean }): UseTopUpReturn => {
  const readOnMount = options?.readOnMount ?? true;
  const [balances, setBalances] = useState<Record<string, MessagingBalance>>({});
  const [balanceLoaded, setBalanceLoaded] = useState(false);
  const [bundles, setBundles] = useState<AppAddonProduct[]>([]);
  const [loadingBundles, setLoadingBundles] = useState(false);
  const [intent, setIntent] = useState<AppAddonPaymentIntent | null>(null);
  const [phase, setPhase] = useState<TopUpPhase>("idle");
  const [arrivedCredits, setArrivedCredits] = useState(0);
  const [lastError, setLastError] = useState<OpResult | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [discount, setDiscount] = useState<AppliedDiscount | null>(null);
  /**
   * The ACCOUNT's purchased-credit balances, keyed by credit feature code.
   *
   * ⚠ THIS IS WHAT THE POST-PAYMENT POLL WATCHES, and it replaced watching the event-relative
   * messaging balance for two reasons. First, every purchase has been ACCOUNT-WIDE since
   * A2.9.2, so the account wallet is the thing that actually moves. Second, the messaging
   * balance is event-scoped and reports only the two sending counters — so on a surface with
   * no event (the Subscription page) it can never move, and a perfectly successful payment
   * would poll twenty times and then report itself as "taking longer than expected", for ever.
   */
  const [accountCredits, setAccountCredits] = useState<Record<string, number>>({});
  /**
   * ⚠ TRUE ONLY AFTER A SUCCESSFUL WALLET READ, and everything that renders a credit figure must
   * gate on it. ms_auth writes a credit row only when something was granted, so an absent feature
   * after a good read is a true zero — but absent BEFORE one, or after a failure, is unknown.
   * Rendering unknown as "0 left" tells a host holding 29,899 emails that they have none, and the
   * obvious response to that is to buy them again.
   */
  const [accountCreditsLoaded, setAccountCreditsLoaded] = useState(false);

  const eventIdRef = useRef(eventId);
  eventIdRef.current = eventId;
  // One purchase at a time: `reset` starts a new session, and work begun in an OLD one — an intent still being created,
  // a credits poll still running — touches nothing once it is over (#126 review: a payment answered after the host had
  // closed the drawer flipped their NEXT purchase to "arrived" and wiped its intent).
  const sessionRef = useRef(0);
  // The product being bought — for the analytics `purchase` once it completes (its SKU, name and kind).
  const buyingRef = useRef<AppAddonProduct | null>(null);

  const readBalance = useCallback(async (): Promise<Record<string, MessagingBalance>> => {
    const id = eventIdRef.current;

    if (!id) {
      return {};
    }

    const result = await EventGuestService.messagingBalance(id);

    if (result.code !== OP_RESULT_CODES.OK || !Array.isArray(result.data)) {
      // ⚠ Leave the previous numbers alone rather than zeroing them. A blip must not tell a
      // host their allowance vanished.
      return {};
    }

    const next: Record<string, MessagingBalance> = {};

    for (const row of result.data as MessagingBalance[]) {
      if (row?.kind) {
        next[row.kind] = row;
      }
    }

    setBalances(next);
    setBalanceLoaded(true);

    return next;
  }, []);

  /** The account wallet. Works with or without an event — there is nothing event-shaped in it. */
  const readAccountCredits = useCallback(async (): Promise<Record<string, number>> => {
    const result = await AppFeatureCreditService.mine();

    if (result.code !== OP_RESULT_CODES.OK || !Array.isArray(result.data)) {
      // ⚠ Same rule as the messaging balance: leave the previous numbers alone rather than
      // zeroing them. A blip must not read as "your credits are gone".
      return {};
    }

    const next: Record<string, number> = {};

    for (const row of result.data as AppFeatureCreditRow[]) {
      if (row?.featureCode && typeof row.remaining === "number") {
        // `remaining`, not `granted` — the spendable figure.
        next[row.featureCode] = row.remaining;
      }
    }

    setAccountCredits(next);
    setAccountCreditsLoaded(true);

    return next;
  }, []);

  useEffect(() => {
    // ⚠ The account wallet is read ALWAYS; the event-relative sending balance only when there
    // is an event to relate it to. The drawer needs the first to confirm a purchase landed and
    // the second only to show "you have N left" beside the bundles.
    if (!readOnMount) {
      return;
    }

    void readAccountCredits();

    if (!eventId) {
      return;
    }

    void readBalance();
  }, [eventId, readOnMount, readBalance, readAccountCredits]);

  const loadBundles = useCallback((params: { featureCode?: string; addonType?: string }) => {
    setLoadingBundles(true);

    void (async () => {
      const result = await AppAddonService.catalog(params);

      if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
        // ⚠ Cheapest first, and TOTALLY ORDERED — a host mid-send wants to unblock the send,
        // not run a procurement, and a list that reshuffles between loads reads as a glitch on
        // a screen that takes money. The rule is a pure function so `npm run check-usage` can
        // exercise it; it used to sort on `credits`, which tied for two services priced very
        // differently and let the server's row order decide what came first.
        setBundles(sortPurchasables(result.data as AppAddonProduct[]));
      } else {
        setBundles([]);
        setLastError(result);
      }

      setLoadingBundles(false);
    })();
  }, []);

  /** The analytics `purchase` for what is being bought, at what was charged (`utils/analytics/purchase.ts`). */
  const reportPurchase = useCallback((paid: AppAddonPaymentIntent | null) => {
    const product = buyingRef.current;

    trackPurchase({
      transactionId: paid?.paymentIntentId,
      kind: String(product?.addonType ?? "").toUpperCase() === "SERVICE" ? PURCHASE_KIND.SERVICE : PURCHASE_KIND.ADDON,
      itemId: product?.providerSku,
      itemName: product?.name,
      amount: paid?.amount,
      currency: paid?.currency,
      coupon: paid?.discountCode,
    });
  }, []);

  const startPurchase = useCallback(
    async (sku: string, discountCode?: string | null) => {
      const session = sessionRef.current;
      buyingRef.current = bundles.find((b) => b.providerSku === sku) ?? { providerSku: sku };
      setPhase("creating");
      setLastError(null);
      setErrorText(null);
      setDiscount(null);

      // ⚠ NO SCOPE ARGUMENT — the credits land on the account (A2.9.2). This call used to pass a
      // `scopeRef` read through a ref, because a stale closure would have put money in the right
      // amount on the WRONG wallet and nothing would ever have reported it. With one destination
      // there is no capture to get wrong.
      //
      // ⚠ The CODE goes up; the PRICE comes back. Nothing here computes a discount — a refused
      // code returns a failed OpResult whose message names the reason, which is worth showing
      // verbatim ("that code has expired" is actionable; "invalid" is not).
      const result = await AppAddonService.createPaymentIntent(sku, discountCode);

      if (session !== sessionRef.current) {
        return;
      }

      if (result.code !== OP_RESULT_CODES.OK) {
        setLastError(result);
        setPhase("idle");

        return;
      }

      const created = (Array.isArray(result.data) ? result.data[0] : null) as AppAddonPaymentIntent | null;

      if (created?.discountCode) {
        setDiscount({
          code: created.discountCode,
          amountBeforeDiscount: Number(created.amountBeforeDiscount) || 0,
          discountAmount: Number(created.discountAmount) || 0,
          amountDue: Number(created.amount) || 0,
          currency: String(created.currency || "USD"),
        });
      }

      // ⚠ NOTHING TO PAY — a code covered the whole bundle and the SERVER already granted. This
      // is the one path where a purchase completes without Stripe, so it must not fall through
      // to the clientSecret check below (which would report a perfectly successful free
      // purchase as a failure).
      //
      // ⚠ Read the FLAG, never `amount === 0`. An amount too small for Stripe to charge is a
      // refusal on the server, not a free grant, and conflating them here would invent a free
      // purchase out of a refusal.
      if (created?.granted) {
        // The balance has already moved server-side; re-read it rather than adding locally, so
        // what is shown is what the server says. Both reads, because the caller may be showing
        // either — and the account wallet is the one that definitely changed.
        await readAccountCredits();
        await readBalance();
        if (session !== sessionRef.current) {
          return;
        }

        setArrivedCredits(Number(created.creditsGranted) || 0);
        setIntent(null);
        setPhase("arrived");
        // A code made it free: still a purchase (value 0). ⓘ No payment id exists — the grant is its own transaction.
        reportPurchase({ ...created, paymentIntentId: created.paymentIntentId || `granted:${sku}:${Date.now()}`, amount: 0 });

        return;
      }

      if (!created?.clientSecret || !created.publishableKey) {
        setLastError(result);
        setPhase("idle");

        return;
      }

      setIntent(created);
      setPhase("paying");
    },
    [readBalance, readAccountCredits, bundles, reportPurchase],
  );

  /**
   * Stripe says the money is taken. Confirm it (ms_auth reads it from Stripe and grants), then wait for the credits.
   *
   * ⚠ Captures the CURRENT credit figure first and waits for it to MOVE, so a host who already
   * had credits is not told their new ones arrived before they did.
   */
  const onPaid = useCallback(
    async (featureCode: string) => {
      const session = sessionRef.current;
      setPhase("confirming");
      // Stripe took the card: the purchase is made (the credits arriving is the grant, not the sale).
      reportPurchase(intent);

      // ⚠ THE ACCOUNT WALLET, not the event balance. Purchases are account-wide (A2.9.2), and
      // this is the only figure that exists on every surface the drawer opens from.
      //
      // ⚠ IT TAKES THE FEATURE CODE, NOT A `TopUpKind`, AND THAT IS A BUG FIX. A kind only maps to
      // the three CREDIT features (message/email/ai) — a SERVICE grants `event_setup`,
      // `quick_start_call` or `concierge_hours`, none of which a kind can name. So a service
      // purchase polled `message_credits`, watched a number that could never move, and told the
      // buyer "the credits are taking a little longer than usual" every single time — while the
      // grant, the ledger row and both emails had already completed correctly.
      //
      // ⚠ THE PRODUCT'S OWN `featureCode` IS THE AUTHORITY. It is on every catalog row, so the
      // caller passes what the purchase actually grants instead of re-deriving it from the drawer's
      // mode. Same lesson as the Subscription page's poll, which watched an event-scoped balance on
      // a page that had no event: **poll the thing the purchase moves.**
      const feature = featureCode;
      const before = accountCredits[feature] ?? 0;
      let attempts = 0;

      // Record the purchase now (ms_auth reads the intent from Stripe), rather than wait for the webhook to arrive. Its
      // verdict is not read: the webhook still grants if this fails, and the poll below is what says the credits are in.
      if (intent?.paymentIntentId) {
        await AppAddonService.confirmPayment(intent.paymentIntentId).catch(() => null);

        if (session !== sessionRef.current) {
          return;
        }
      }

      const tick = async (): Promise<void> => {
        attempts += 1;
        const now = await readAccountCredits();
        if (session !== sessionRef.current) {
          return;
        }

        // ⚠ Fall back to `before`, not 0 — a failed poll must read as "no movement yet", never
        // as the balance having dropped to nothing.
        const after = now[feature] ?? before;

        // ⚠ Waits for MOVEMENT, not for a non-zero figure. A host who already had credits would
        // otherwise be told their purchase landed the instant they paid.
        if (after > before) {
          setArrivedCredits(after - before);
          setIntent(null);
          setPhase("arrived");
          // Keep the event-relative display in step with the wallet that just moved.
          void readBalance();

          return;
        }

        if (attempts >= POLL_ATTEMPTS) {
          // ⚠ The payment SUCCEEDED. Only the grant is late — say that, never "failed".
          setIntent(null);
          setPhase("slow");

          return;
        }

        await new Promise((resolve) => window.setTimeout(resolve, POLL_MS));
        if (session !== sessionRef.current) {
          return;
        }

        return tick();
      };

      await tick();
    },
    [accountCredits, readAccountCredits, readBalance, intent, reportPurchase]
  );

  const purchasedCredits = useCallback(
    (kind: TopUpKind): number | undefined => {
      if (!accountCreditsLoaded) {
        return undefined;
      }

      // Absent AFTER a successful read is a true zero — the feature has simply never been
      // topped up. That is a different fact from "not known", which the guard above returns.
      return accountCredits[creditFeatureForKind(kind)] ?? 0;
    },
    [accountCredits, accountCreditsLoaded],
  );

  const balance = useCallback(
    (kind: TopUpKind): MessagingBalance | undefined => {
      // ⚠ `ai` has no row here by design — `eventMessagingBalance` reports SENDING counters.
      // Returning undefined is the honest answer, and the drawer already treats undefined as
      // "not known" rather than zero.
      return balances[kind];
    },
    [balances]
  );

  const reset = useCallback(() => {
    sessionRef.current += 1;
    setIntent(null);
    setPhase("idle");
    setArrivedCredits(0);
    setLastError(null);
    setErrorText(null);
    setDiscount(null);
  }, []);

  return {
    balance,
    purchasedCredits,
    balanceLoaded,
    reloadBalance: async () => {
      await readAccountCredits();
      await readBalance();
    },
    bundles,
    loadingBundles,
    loadBundles,
    intent,
    phase,
    arrivedCredits,
    lastError,
    errorText,
    startPurchase,
    discount,
    onPaid,
    setPaymentError: setErrorText,
    reset,
  };
};
